// S3 BOM editor — /catalogue/products/:id/boms/:bomId (Module 01 §4 S3), on
// the revision-6 register.
//
// One page head (breadcrumbs → the product → this version), one toolbar row for
// version switching/compare, one state banner (draft vs read-only, the M01-S07/
// S10 selectors `.bom-banner-draft` / `.bom-banner-readonly`), then three
// Cards: the batch header, the component tree | line inspector grid, and the
// outputs panel. A draft carries an Activate dialog (effective date, required
// note, live overlap preview); a non-draft version is read-only with a
// Create-new-version CTA.
//
// Doctrine: data flows only through @assessly/api-client (the catalogue-queries
// hooks); cost surfaces (by-product NRV) render only when the API returns the
// field (§7); write affordances show only for a draft (the spec's read-only
// rule) and the server remains the authz backstop — its problem+json is
// surfaced in a §3.8 banner carrying the request id.
//
// SCHEDULED + WITHDRAW (§3.3.5, §9.16, D-261). An `active` version whose
// `effective_from` is still ahead of today is NOT in force, and this screen
// used to say "Active" about it while §3.4 resolved today to an older version
// — the 2030-dated KG-3x70 lock. Now: the header chip reads "Scheduled", the
// state banner is amber and names the date, a line under the timeline states
// which version the resolver actually answers for TODAY, and a Withdraw
// control (catalogue.bom.activate, the same code as activate) opens the
// shared dialog. "Today" is the FACILITY's calendar where the version is
// facility-scoped, else the first active facility's, else the browser's
// (D-089); the server re-validates in the tenant's timezone and its three
// refusals are rendered by name in the dialog.
//
// ARCHIVE (§3.3.5 Archive; D-261 amended 2026-09-08). A draft is abandoned,
// a superseded version housekept — never deleted, never undone — through
// the shared ArchiveDialog, gated on the same §7 code the API gates
// `POST /boms/:id/archive` on (catalogue.bom.activate). The two refusals are
// rendered by name: VERSION_STILL_RESUMABLE (the row's own range has not
// ended — withdraw the scheduled successor first) and BOM_NOT_ARCHIVABLE
// (the status moved before the write). Both dialogs' previews are captured
// when they OPEN, and both results take focus when they land (ResultFocus).

import { enumLabel } from "../../../../lib/enum-label";
import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Card } from "../../../../components/card";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner, toast } from "../../../../components/toast";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import { requestIdOf } from "../../../../lib/request";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { EffectivityTimeline, type EffectivityVersion } from "../components/effectivity-timeline";
import { EffectiveTodayNote, effectiveTodayStateOf } from "../components/effective-today";
import { VersionSwitcher, type VersionOption } from "../components/version-switcher";
import {
  archivePreview,
  isScheduledVersion,
  restorePreview,
  toRestoreSummary,
  type ArchivePreview,
  type RestoreSummary,
} from "../components/version-schedule";
import { ArchiveDialog } from "../components/archive-dialog";
import { ResultFocus } from "../components/result-focus";
import { WithdrawDialog } from "../components/withdraw-dialog";
import {
  useActivateBom,
  useArchiveBom,
  useBom,
  useCreateBomVersion,
  useEffectiveBom,
  useFacilities,
  useProduct,
  useProductBoms,
  useProducts,
  useReplaceBomLines,
  useReplaceBomOutputs,
  useUoms,
  useUpdateBom,
  useWithdrawBom,
} from "../hooks/catalogue-queries";
import { useCataloguePermissions } from "../hooks/permissions";
import { BomTree } from "./bom-editor/tree";
import { LineInspector } from "./bom-editor/line-inspector";
import { OutputsPanel } from "./bom-editor/outputs-panel";
import { ActivateDialog } from "./bom-editor/activate-dialog";
import { BomDiff } from "./bom-editor/bom-diff";
import { indexProducts, makeUomLabeller } from "./bom-editor/pickers";
import {
  buildBomTree,
  computeApportionment,
  fromBomLine,
  fromBomOutput,
  nextLineNo,
  toLineInput,
  toOutputInput,
  type EditableLine,
  type EditableOutput,
} from "./bom-editor/helpers";
import "../catalogue.css";

type Bom = components["schemas"]["Bom"];
type Product = components["schemas"]["Product"];
type Uom = components["schemas"]["Uom"];
type BomSummary = components["schemas"]["BomSummary"];

export function BomEditorScreen() {
  const { t } = useTranslation();
  const params = useParams({ strict: false });
  const productId = params.id ?? "";
  const bomId = params.bomId ?? "";

  const productQ = useProduct(productId);
  const bomQ = useBom(bomId);
  const explodeQ = useBom(bomId, { explode: true });
  const versionsQ = useProductBoms(productId);
  const productsQ = useProducts({ limit: 200 });
  const uomsQ = useUoms();

  if (
    productQ.isPending ||
    bomQ.isPending ||
    versionsQ.isPending ||
    productsQ.isPending ||
    uomsQ.isPending
  ) {
    return (
      <section className="bom-editor">
        <CardSkeleton lines={6} label={t("catalogue.bomEditor.loadingLabel")} />
      </section>
    );
  }

  const failing = productQ.error ?? bomQ.error ?? versionsQ.error ?? productsQ.error ?? uomsQ.error;
  if (failing) {
    const detail = failing instanceof Error ? failing.message : undefined;
    const requestId = requestIdOf(failing);
    return (
      <section className="bom-editor">
        <ErrorState
          title={t("catalogue.bomEditor.errorTitle")}
          {...(detail !== undefined ? { detail } : {})}
          retryLabel={t("catalogue.state.retry")}
          onRetry={() => {
            void bomQ.refetch();
            void versionsQ.refetch();
          }}
          {...(requestId !== undefined ? { requestId } : {})}
          requestIdLabel={t("catalogue.state.requestId")}
        />
      </section>
    );
  }

  const product = productQ.data;
  const bomData = bomQ.data;
  const versionsData = versionsQ.data;
  const productsData = productsQ.data;
  const uomsData = uomsQ.data;
  if (
    product === undefined ||
    bomData === undefined ||
    versionsData === undefined ||
    productsData === undefined ||
    uomsData === undefined ||
    !("lines" in bomData)
  ) {
    return (
      <section className="bom-editor">
        <ErrorState
          title={t("catalogue.bomEditor.errorTitle")}
          retryLabel={t("catalogue.state.retry")}
          onRetry={() => void bomQ.refetch()}
        />
      </section>
    );
  }

  const explodeNodes =
    explodeQ.data !== undefined && "nodes" in explodeQ.data ? explodeQ.data.nodes : [];

  return (
    <BomEditorInner
      key={bomData.id}
      productId={productId}
      product={product}
      bom={bomData}
      explodeNodes={explodeNodes}
      versions={versionsData.items}
      products={productsData.items}
      uoms={uomsData.items}
    />
  );
}

function BomEditorInner({
  productId,
  product,
  bom,
  explodeNodes,
  versions,
  products,
  uoms,
}: {
  productId: string;
  product: Product;
  bom: Bom;
  explodeNodes: readonly components["schemas"]["BomExplosionNode"][];
  versions: readonly BomSummary[];
  products: readonly Product[];
  uoms: readonly Uom[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [lines, setLines] = useState<EditableLine[]>(() => bom.lines.map(fromBomLine));
  const [outputs, setOutputs] = useState<EditableOutput[]>(() => bom.outputs.map(fromBomOutput));
  const [outputQty, setOutputQty] = useState<number>(bom.output_qty);
  const [outputUomId, setOutputUomId] = useState<string>(bom.output_uom_id);
  const [bomName, setBomName] = useState<string>(bom.name ?? "");
  const [headerDirty, setHeaderDirty] = useState(false);
  const [selectedLineNo, setSelectedLineNo] = useState<number | null>(
    bom.lines[0]?.line_no ?? null,
  );
  const [linesDirty, setLinesDirty] = useState(false);
  const [outputsDirty, setOutputsDirty] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [compareBomId, setCompareBomId] = useState<string>("");
  /* The effective date the in-session activation was given. Set the moment
   * the mutation lands (before the refetch replaces `bom`), so the banner can
   * say "scheduled for 2030-06-10" instead of the old, unconditional "now
   * effective" — which was a lie for every future date (D-261). */
  const [activatedFrom, setActivatedFrom] = useState<string | null>(null);
  /* The withdraw dialog is open while this holds the restore preview CAPTURED
   * at the moment it opened. A refetch landing mid-dialog (a background
   * revalidation, or the one a refusal triggers) used to recompute the
   * preview live under the planner's decision — "Version 1 will return"
   * could flip to "nothing will be restored" with the reason half typed. */
  const [withdrawDialog, setWithdrawDialog] = useState<RestoreSummary | null>(null);
  /* Same shape for the archive dialog: what archiving meant when it opened.
   * Here the capture also keeps the dialog MOUNTED through a NOT_ARCHIVABLE
   * refusal — the hook refetches, the row reads `active`, and a live
   * condition would have unmounted the dialog with its refusal unread. */
  const [archiveDialog, setArchiveDialog] = useState<ArchivePreview | null>(null);
  /* ONE saved-notice per screen (§3.8): a header/lines/outputs save all land in
   * the same banner, so a user never has to reconcile three little green
   * fragments. It clears the moment the draft is edited again. */
  const [saved, setSaved] = useState<string | null>(null);

  const replaceLines = useReplaceBomLines(bom.id);
  const replaceOutputs = useReplaceBomOutputs(bom.id);
  const updateBom = useUpdateBom(bom.id);
  const activateBom = useActivateBom(bom.id, productId);
  const withdrawBom = useWithdrawBom(bom.id, productId);
  /* The withdraw RESULT is the mutation's OWN data, not a copy taken in a
   * `mutate()` callback. A per-call `onSuccess` fires only while the
   * observer is still attached: an Escape mid-flight used to call `reset()`,
   * the callback was dropped, the request landed anyway, and the screen kept
   * a "Scheduled" chip and a Withdraw button on a row that was now archived.
   * The dialog now refuses every exit while pending (so `reset()` cannot
   * run under a live request), AND the result is read from `data`, so there
   * is no callback left to lose. `restoredVersionId === null` is the legal
   * "no active version now" state, rendered as a fact to act on. Dismissing
   * the banner is `reset()`. */
  const withdrawn =
    withdrawBom.data === undefined
      ? null
      : {
          withdrawnVersion: withdrawBom.data.bom.version,
          restoredVersionId: withdrawBom.data.restored_version_id,
        };
  /* The in-session activation is undone by a withdrawal: without this, the
   * transitional `activatedFrom > today` clause below would keep the banner
   * reading "Scheduled" on a row that is now archived. Derived, not cleared
   * in a callback — for the reason the result itself is derived. */
  const activatedFromLive = withdrawn === null ? activatedFrom : null;
  const activated = activatedFromLive !== null;
  const archiveBom = useArchiveBom(bom.id, productId);
  /* The archive RESULT, read the same way as the withdraw result: the
   * mutation's own data, so no exit can drop it. */
  const archived = archiveBom.data === undefined ? null : { version: archiveBom.data.version };
  const createVersion = useCreateBomVersion(productId);

  // "Today" for the scheduled/in-force question (D-089: data selection is
  // facility-local). A facility-scoped version takes that facility's calendar;
  // a group-wide one the first active facility's; with no facilities in hand
  // yet, the browser's — the documented degradation of `facilityTodayIso`.
  // The facilities read is a cached reference lookup and never gates the
  // screen: a version page that waits on it is worse than one right 19 hours
  // a day. The server re-validates every withdraw in the tenant's timezone.
  const facilitiesQ = useFacilities();
  const calendarFacility =
    facilitiesQ.data?.items.find((f) => f.id === bom.facility_id) ?? facilitiesQ.data?.items[0];
  const today = facilityTodayIso(Date.now(), calendarFacility?.timezone);

  // Which version the §3.4 resolver answers for TODAY — asked of the server,
  // not re-derived here, so this line and the cost roll-up agree.
  const effectiveQ = useEffectiveBom(
    productId,
    bom.facility_id !== null ? { facility_id: bom.facility_id, date: today } : { date: today },
  );

  // §7 authoring gate — the server enforces catalogue.bom.write/.activate; these
  // hide the authoring + activate affordances from a role that can only read the
  // structure (e.g. finance/qc/procurement), so a draft opens read-only for them.
  const perms = useCataloguePermissions();
  const canWrite = perms.has("catalogue.bom.write");
  const canActivatePerm = perms.has("catalogue.bom.activate");

  const readOnly = bom.status !== "draft" || activated || !canWrite;
  /* What archiving THIS row would mean (§3.3.5) — null when it is not offered:
   * an active version is superseded or withdrawn, never archived; an archived
   * one is terminal; a draft activated in this session is active on the wire
   * the moment the refetch lands. Gated on catalogue.bom.activate, the code
   * the API requires for `POST /boms/:id/archive`. */
  const archivePlan =
    archived === null && !activated ? archivePreview(bom.status, bom.effective_to, today) : null;
  const canArchive = archivePlan !== null && canActivatePerm;
  // Active but not yet in force (§9.16). The transitional clause covers the
  // moment between a future-dated activation landing and the refetch.
  const scheduled =
    isScheduledVersion(bom.status, bom.effective_from, today) ||
    (activatedFromLive !== null && activatedFromLive > today);
  const scheduledFrom = bom.effective_from ?? activatedFromLive ?? today;
  // Before the refetch lands, the in-session date is the one the predecessor
  // was closed against (§3.3.4), so the preview reads it where the row has
  // none yet.
  const withdrawRestore = toRestoreSummary(
    restorePreview(versions, {
      id: bom.id,
      effective_from: bom.effective_from ?? activatedFromLive,
      facility_id: bom.facility_id,
    }),
  );
  const productsById = indexProducts(products);
  const uomLabel = makeUomLabeller(uoms);
  const tree = buildBomTree(lines, explodeNodes, productsById);
  const selectedLine = lines.find((l) => l.line_no === selectedLineNo);

  // NRV redaction (§7): the by-product NRV surface renders only when the loaded
  // outputs carry the field (present ⇒ the caller has cost.read). With no
  // existing by-products there is nothing to test, so default to visible and let
  // the server enforce on save.
  const existingByProducts = bom.outputs.filter((o) => o.output_kind === "by_product");
  const costVisible =
    existingByProducts.length === 0
      ? true
      : existingByProducts.some((o) => o.nrv_per_price_unit_minor !== undefined);

  const dirty = linesDirty || outputsDirty || headerDirty;

  const reseed = (next: Bom) => {
    setLines(next.lines.map(fromBomLine));
    setOutputs(next.outputs.map(fromBomOutput));
    setOutputQty(next.output_qty);
    setOutputUomId(next.output_uom_id);
    setBomName(next.name ?? "");
    setLinesDirty(false);
    setOutputsDirty(false);
    setHeaderDirty(false);
  };

  // Mutations use `.mutate(body, { onSuccess })` (not a bare `void (async …)()`
  // over mutateAsync) so a rejected request surfaces on the mutation's `.error`
  // (rendered below) instead of becoming an unhandled promise rejection.
  const saveHeader = () => {
    updateBom.mutate(
      {
        output_qty: outputQty,
        output_uom_id: outputUomId,
        name: bomName.trim().length === 0 ? null : bomName.trim(),
      },
      {
        onSuccess: (next) => {
          if ("lines" in next) reseed(next);
          setSaved(t("catalogue.bomEditor.savedHeader"));
        },
      },
    );
  };

  const onLineChange = (next: EditableLine) => {
    setLines((prev) => prev.map((l) => (l.key === next.key ? next : l)));
    setLinesDirty(true);
    setSaved(null);
  };

  const addLine = () => {
    const line: EditableLine = {
      key: `line-new-${nextLineNo(lines)}-${lines.length}`,
      id: undefined,
      line_no: nextLineNo(lines),
      component_product_id: "",
      qty: 1,
      uom_id: uoms[0]?.id ?? "",
      scrap_pct: 0,
      consume_at_step_no: null,
      is_optional: false,
      notes: null,
      substitutes: [],
    };
    setLines((prev) => [...prev, line]);
    setSelectedLineNo(line.line_no);
    setLinesDirty(true);
    setSaved(null);
  };

  const removeSelectedLine = () => {
    if (selectedLine === undefined) return;
    setLines((prev) => prev.filter((l) => l.key !== selectedLine.key));
    setSelectedLineNo(null);
    setLinesDirty(true);
    setSaved(null);
  };

  const saveLines = () => {
    replaceLines.mutate(
      { lines: lines.map(toLineInput) },
      {
        onSuccess: (next) => {
          if ("lines" in next) reseed(next);
          setSaved(t("catalogue.bomEditor.savedLines", { count: lines.length }));
        },
      },
    );
  };

  const saveOutputs = () => {
    replaceOutputs.mutate(
      { outputs: outputs.map(toOutputInput) },
      {
        onSuccess: (next) => {
          if ("lines" in next) reseed(next);
          setSaved(t("catalogue.bomEditor.savedOutputs", { count: outputs.length }));
        },
      },
    );
  };

  const activate = (effectiveFrom: string, note: string) => {
    activateBom.mutate(
      { effective_from: effectiveFrom, activation_note: note },
      {
        onSuccess: () => {
          setActivatedFrom(effectiveFrom);
          setDialogOpen(false);
          setSaved(null);
        },
      },
    );
  };

  // No per-call callbacks: the result and the dialog's closing both derive
  // from the mutation's data (see `withdrawn` above).
  const withdraw = (reason: string) => {
    withdrawBom.mutate({ reason });
  };

  const archive = () => {
    archiveBom.mutate();
  };

  const goToVersion = (id: string) => {
    if (id === bom.id) return;
    void navigate({
      to: "/catalogue/products/$id/boms/$bomId",
      params: { id: productId, bomId: id },
    });
  };

  const createNewVersion = () => {
    createVersion.mutate(
      { copy_from_version_id: bom.id },
      {
        onSuccess: (created) => {
          /* §3.8 — this WRITES a row and changes the URL. It used to confirm
           * nothing at all, so two clicks produced draft v3 AND v4 with the
           * user unable to tell either had happened. A toast (not an inline
           * banner) because the navigation below unmounts this screen. */
          toast.success(
            t("catalogue.bomEditor.versionCreated", {
              version: created.version,
              from: bom.version,
            }),
          );
          void navigate({
            to: "/catalogue/products/$id/boms/$bomId",
            params: { id: productId, bomId: created.id },
          });
        },
      },
    );
  };

  const versionOptions: VersionOption[] = versions.map((v) => ({
    id: v.id,
    version: v.version,
    status: v.status,
    effective_from: v.effective_from,
  }));
  const restoredVersionId = withdrawn?.restoredVersionId ?? null;
  const restoredVersion =
    restoredVersionId === null ? undefined : versions.find((v) => v.id === restoredVersionId);
  const effectivityVersions: EffectivityVersion[] = versions.map((v) => ({
    id: v.id,
    version: v.version,
    status: v.status,
    effective_from: v.effective_from,
    effective_to: v.effective_to,
  }));
  const compareOptions = versions.filter((v) => v.id !== bom.id);

  // Apportionment guard (§2.9, §9.5): Σ co-product pct must stay < 100 and each
  // pct ∈ (0,100). The server rejects a violating set with APPORTIONMENT_SUM_INVALID
  // at activation; mirror it up front by blocking Save-outputs and Activate so the
  // draft cannot be pushed into a doomed state.
  const coProductOutputs = outputs.filter((o) => o.output_kind === "co_product");
  const apportionment = computeApportionment(coProductOutputs);

  const canActivate =
    !readOnly &&
    !dirty &&
    lines.length > 0 &&
    !activateBom.isPending &&
    apportionment.valid &&
    canActivatePerm;
  const linesHaveComponent = lines.every((l) => l.component_product_id.length > 0);

  /* §1 rule 1 — EXACTLY ONE primary on this screen. The draft used to paint
   * three: Activate plus both section saves, two of them disabled, which is a
   * disabled control claiming the loudest emphasis on the page. The primary is
   * now ranked by reading order — the first section with pending edits owns it,
   * and with nothing pending it belongs to Activate (which `canActivate`
   * already requires `!dirty` for). Every save stays clickable; only the
   * emphasis moves. */
  const primaryAction: "header" | "lines" | "outputs" | "activate" = headerDirty
    ? "header"
    : linesDirty
      ? "lines"
      : outputsDirty
        ? "outputs"
        : "activate";

  const mutationError =
    (createVersion.error instanceof Error ? createVersion.error : undefined) ??
    (replaceLines.error instanceof Error ? replaceLines.error : undefined) ??
    (replaceOutputs.error instanceof Error ? replaceOutputs.error : undefined) ??
    (activateBom.error instanceof Error ? activateBom.error : undefined) ??
    (updateBom.error instanceof Error ? updateBom.error : undefined);

  return (
    <section className="bom-editor cat-screen" data-testid="bom-editor">
      <PageHeader
        title={t("catalogue.bomEditor.version", { version: bom.version })}
        crumbs={[
          { label: t("nav.catalogue"), to: "/catalogue/products" },
          { label: t("catalogue.productsList.title"), to: "/catalogue/products" },
          { label: product.sku, to: "/catalogue/products" },
        ]}
        actions={
          <>
            {scheduled ? (
              /* The wire status is `active`; the human meaning is "not yet".
               * One chip, so the header never says both (§9.16, D-261). */
              <StatusChip variant="warn" data-status="scheduled" data-testid="version-scheduled">
                {t("catalogue.status.scheduled")}
              </StatusChip>
            ) : (
              <StatusBadge status={bom.status} />
            )}
            {bom.is_phantom ? (
              <StatusChip variant="neutral" dot={false}>
                {t("catalogue.bomEditor.phantom")}
              </StatusChip>
            ) : null}
          </>
        }
      />

      <div className="ds-toolrow">
        <VersionSwitcher
          versions={versionOptions}
          value={bom.id}
          today={today}
          onChange={goToVersion}
        />
        {compareOptions.length > 0 ? (
          <label className="bom-compare">
            <span>{t("catalogue.bomEditor.diff.compareAgainst")}</span>
            <Select
              value={compareBomId}
              onChange={(e) => {
                setCompareBomId(e.target.value);
              }}
            >
              <option value="">{t("catalogue.bomEditor.diff.selectVersion")}</option>
              {compareOptions.map((v) => (
                <option value={v.id} key={v.id}>
                  v{v.version}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
      </div>

      <EffectivityTimeline versions={effectivityVersions} selectedId={bom.id} today={today} />
      <EffectiveTodayNote
        today={today}
        state={effectiveTodayStateOf(effectiveQ)}
        currentVersionId={bom.id}
        onOpen={goToVersion}
        onRetry={() => void effectiveQ.refetch()}
      />

      {/* ONE rendering of "what state is this version in". Both branches are the
       * §3.8 `Banner` primitive; the `.bom-banner-*` class names ride along as
       * the certified M01-S07/S10 selectors. They used to be hand-rolled divs
       * — while the SAME file imported and used the real `Banner` forty lines
       * below — which is how the slice shipped three renderings of one idea.
       *
       * The draft bar is NEUTRAL, not amber: amber means "needs attention"
       * (§1 rule 3), and a draft you are deliberately editing is a normal
       * working state, not a warning. */}
      {readOnly ? (
        /* SCHEDULED is amber (§1 rule 3: needs attention) — a version that is
         * active-but-not-in-force is the one state on this screen a human must
         * not mistake for business as usual. Any other read-only status stays
         * neutral. `.bom-banner-readonly` rides on both: M01-S07 waits for it
         * after an activation, and its date is a future Monday. */
        <Banner
          kind={scheduled ? "warn" : "neutral"}
          className={
            scheduled
              ? "bom-banner bom-banner-readonly bom-banner-scheduled"
              : "bom-banner bom-banner-readonly"
          }
          {...(scheduled ? { "data-testid": "bom-scheduled-banner" } : {})}
          aside={
            <span className="bom-banner-actions">
              {/* Withdraw is gated on the SAME §7 code as activate. */}
              {scheduled && canActivatePerm ? (
                <Button
                  variant="danger"
                  data-testid="withdraw-open"
                  disabled={withdrawBom.isPending}
                  onClick={() => {
                    setWithdrawDialog(withdrawRestore);
                  }}
                >
                  {t("catalogue.withdraw.button")}
                </Button>
              ) : null}
              {/* Housekeeping (a superseded version) — the same §7 code. */}
              {canArchive ? (
                <Button
                  variant="danger"
                  data-testid="archive-open"
                  disabled={archiveBom.isPending}
                  onClick={() => {
                    setArchiveDialog(archivePlan);
                  }}
                >
                  {t("catalogue.archive.button")}
                </Button>
              ) : null}
              {/* Starting a new draft version needs catalogue.bom.write (§7). */}
              {canWrite ? (
                <Button
                  variant="primary"
                  busy={createVersion.isPending}
                  busyLabel={t("catalogue.bomEditor.creatingVersion")}
                  onClick={createNewVersion}
                >
                  {t("catalogue.bomEditor.createNewVersion")}
                </Button>
              ) : null}
            </span>
          }
        >
          {scheduled
            ? activated
              ? t("catalogue.bomEditor.activate.scheduled", { date: scheduledFrom })
              : t("catalogue.bomEditor.scheduledBanner", { date: scheduledFrom })
            : activated
              ? t("catalogue.bomEditor.activate.success")
              : t("catalogue.bomEditor.readOnlyBanner", {
                  status: enumLabel(t, "catalogue.status", bom.status),
                })}
        </Banner>
      ) : (
        <Banner
          kind="neutral"
          className="bom-banner bom-banner-draft"
          aside={
            <span className="bom-banner-actions">
              {dirty ? (
                <span className="bom-hint">{t("catalogue.bomEditor.activate.saveFirst")}</span>
              ) : null}
              {!apportionment.valid ? (
                <span className="bom-hint" data-testid="activate-apportionment-hint">
                  {t("catalogue.bomEditor.activate.fixApportionment")}
                </span>
              ) : null}
              {/* Abandon the draft (§3.3.5) — gated as the API gates it. */}
              {canArchive ? (
                <Button
                  variant="danger"
                  data-testid="archive-open"
                  disabled={archiveBom.isPending}
                  onClick={() => {
                    setArchiveDialog(archivePlan);
                  }}
                >
                  {t("catalogue.archive.button")}
                </Button>
              ) : null}
              <Button
                variant={primaryAction === "activate" ? "primary" : "secondary"}
                disabled={!canActivate}
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                {t("catalogue.bomEditor.activate.button")}
              </Button>
            </span>
          }
        >
          {t("catalogue.bomEditor.draftBanner")}
        </Banner>
      )}

      {saved !== null ? (
        <Banner
          kind="ok"
          data-testid="bom-saved"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setSaved(null);
          }}
        >
          {saved}
        </Banner>
      ) : null}

      {/* The withdraw RESULT (§3.8: the primary record is inline). A null
       * `restored_version_id` is the legal no-active-version state — amber,
       * because the next thing that needs a version will not find one. It
       * takes focus as it lands (the dialog's opener is about to vanish). */}
      {withdrawn !== null ? (
        <ResultFocus testId="bom-withdrawn-focus">
          <Banner
            kind={withdrawn.restoredVersionId === null ? "warn" : "ok"}
            data-testid="bom-withdrawn"
            data-restored={withdrawn.restoredVersionId === null ? "none" : "version"}
            dismissLabel={t("catalogue.state.dismiss")}
            onDismiss={() => {
              // Both in one handler, so no render sees the dialog re-open on
              // a reset mutation with the dialog state still set.
              setWithdrawDialog(null);
              withdrawBom.reset();
            }}
            aside={
              restoredVersion !== undefined ? (
                <Button
                  size="sm"
                  onClick={() => {
                    goToVersion(restoredVersion.id);
                  }}
                >
                  {t("catalogue.withdraw.openRestored", { version: restoredVersion.version })}
                </Button>
              ) : undefined
            }
          >
            {withdrawn.restoredVersionId === null
              ? t("catalogue.withdraw.resultNone", { withdrawn: withdrawn.withdrawnVersion })
              : restoredVersion !== undefined
                ? t("catalogue.withdraw.resultRestored", {
                    withdrawn: withdrawn.withdrawnVersion,
                    restored: restoredVersion.version,
                  })
                : // Not in the list on screen: name the id, never a blank.
                  t("catalogue.withdraw.resultRestoredById", {
                    withdrawn: withdrawn.withdrawnVersion,
                    id: withdrawn.restoredVersionId,
                  })}
          </Banner>
        </ResultFocus>
      ) : null}

      {/* The archive RESULT — the same inline record, the same focus move. */}
      {archived !== null ? (
        <ResultFocus testId="bom-archived-focus">
          <Banner
            kind="ok"
            data-testid="bom-archived"
            dismissLabel={t("catalogue.state.dismiss")}
            onDismiss={() => {
              setArchiveDialog(null);
              archiveBom.reset();
            }}
          >
            {t("catalogue.archive.result", { version: archived.version })}
          </Banner>
        </ResultFocus>
      ) : null}

      {mutationError !== undefined ? (
        <Banner kind="bad" data-testid="bom-mutation-error">
          {mutationError.message}
          {requestIdOf(mutationError) !== undefined ? (
            <span className="state-request-id">
              {" "}
              {t("catalogue.state.requestId")}: <code>{requestIdOf(mutationError)}</code>
            </span>
          ) : null}
        </Banner>
      ) : null}

      <Card title={t("catalogue.bomEditor.headerTitle")} data-testid="bom-header">
        <div className="cat-form">
          <label className="cat-fld">
            <span>{t("catalogue.bomEditor.outputQty")}</span>
            <input
              type="number"
              min={0}
              step="any"
              disabled={readOnly}
              value={Number.isNaN(outputQty) ? "" : outputQty}
              onChange={(e) => {
                setOutputQty(e.target.value === "" ? Number.NaN : Number(e.target.value));
                setHeaderDirty(true);
                setSaved(null);
              }}
            />
          </label>
          <label className="cat-fld">
            <span>{t("catalogue.bomEditor.outputUom")}</span>
            <select
              className="bom-picker"
              disabled={readOnly}
              value={outputUomId}
              onChange={(e) => {
                setOutputUomId(e.target.value);
                setHeaderDirty(true);
                setSaved(null);
              }}
            >
              {uoms.map((u) => (
                <option value={u.id} key={u.id}>
                  {u.code}
                </option>
              ))}
            </select>
          </label>
          <label className="cat-fld">
            <span>{t("catalogue.bomEditor.nameLabel")}</span>
            <input
              type="text"
              disabled={readOnly}
              placeholder={t("catalogue.bomEditor.namePlaceholder")}
              value={bomName}
              onChange={(e) => {
                setBomName(e.target.value);
                setHeaderDirty(true);
                setSaved(null);
              }}
            />
          </label>
          {!readOnly ? (
            <div className="cat-form-actions">
              <Button
                variant={primaryAction === "header" ? "primary" : "secondary"}
                disabled={!headerDirty}
                busy={updateBom.isPending}
                busyLabel={t("catalogue.bomEditor.inspector.saving")}
                onClick={saveHeader}
              >
                {t("catalogue.bomEditor.saveHeader")}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      {compareBomId.length > 0 ? (
        <BomDiff
          currentLines={lines}
          currentVersion={bom.version}
          compareBomId={compareBomId}
          productsById={productsById}
          uomLabel={uomLabel}
        />
      ) : null}

      <div className="bom-editor-grid">
        <Card
          title={t("catalogue.bomEditor.tree.heading")}
          count={lines.length}
          action={
            readOnly ? undefined : (
              <Button size="sm" onClick={addLine}>
                {t("catalogue.bomEditor.tree.addLine")}
              </Button>
            )
          }
        >
          <div className="bom-col-body">
            {lines.length === 0 ? (
              readOnly ? (
                <p className="bom-hint">{t("catalogue.bomEditor.outputs.none")}</p>
              ) : (
                <EmptyState
                  title={t("catalogue.bomEditor.emptyLinesTitle")}
                  body={t("catalogue.bomEditor.emptyLinesBody")}
                  ctaLabel={t("catalogue.bomEditor.tree.addLine")}
                  onCta={addLine}
                />
              )
            ) : (
              <BomTree
                nodes={tree}
                selectedLineNo={selectedLineNo}
                onSelectLine={setSelectedLineNo}
                uomLabel={uomLabel}
              />
            )}
            {explodeNodes.length === 0 && lines.length > 0 ? (
              <p className="bom-hint bom-structure-hint">
                {t("catalogue.bomEditor.tree.structureUnavailable")}
              </p>
            ) : null}
          </div>
        </Card>

        <Card
          title={t("catalogue.bomEditor.inspector.heading")}
          action={
            !readOnly && linesDirty ? (
              <span className="bom-dirty">{t("catalogue.bomEditor.inspector.unsaved")}</span>
            ) : undefined
          }
        >
          <div className="bom-col-body">
            <LineInspector
              line={selectedLine}
              products={products}
              uoms={uoms}
              readOnly={readOnly}
              onChange={onLineChange}
              onRemove={removeSelectedLine}
            />
            {!readOnly && lines.length > 0 ? (
              <div className="bom-save-row">
                <Button
                  variant={primaryAction === "lines" ? "primary" : "secondary"}
                  disabled={!linesDirty || !linesHaveComponent}
                  busy={replaceLines.isPending}
                  /* LOAD-BEARING (M01-S04): the walkthrough waits for this
                     control to go disabled as its "the save landed" signal. */
                  busyLabel={t("catalogue.bomEditor.inspector.saving")}
                  onClick={saveLines}
                >
                  {t("catalogue.bomEditor.inspector.save")}
                </Button>
              </div>
            ) : null}
          </div>
        </Card>
      </div>

      <Card
        title={t("catalogue.bomEditor.outputs.heading")}
        action={
          !readOnly && outputsDirty ? (
            <span className="bom-dirty">{t("catalogue.bomEditor.inspector.unsaved")}</span>
          ) : undefined
        }
      >
        <div className="bom-col-body">
          <OutputsPanel
            outputs={outputs}
            products={products}
            uoms={uoms}
            readOnly={readOnly}
            costVisible={costVisible}
            defaultCurrency={product.currency}
            onChange={(next) => {
              setOutputs(next);
              setOutputsDirty(true);
              setSaved(null);
            }}
          />
          {!readOnly ? (
            <div className="bom-save-row">
              <Button
                variant={primaryAction === "outputs" ? "primary" : "secondary"}
                disabled={!outputsDirty || !apportionment.valid}
                busy={replaceOutputs.isPending}
                busyLabel={t("catalogue.bomEditor.outputs.saving")}
                onClick={saveOutputs}
              >
                {t("catalogue.bomEditor.outputs.save")}
              </Button>
              {!apportionment.valid ? (
                <span className="bom-hint" data-testid="outputs-apportionment-hint">
                  {t("catalogue.bomEditor.outputs.saveBlocked")}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {dialogOpen ? (
        <ActivateDialog
          versions={versions}
          draftFacilityId={bom.facility_id}
          activating={activateBom.isPending}
          onActivate={activate}
          onCancel={() => {
            setDialogOpen(false);
          }}
        />
      ) : null}

      {/* Open until the result exists — the dialog closes BECAUSE the data
       * landed, not because a callback said so. */}
      {withdrawDialog !== null && withdrawn === null ? (
        <WithdrawDialog
          version={bom.version}
          effectiveFrom={scheduledFrom}
          restore={withdrawDialog}
          pending={withdrawBom.isPending}
          error={withdrawBom.error}
          onConfirm={withdraw}
          onCancel={() => {
            // Unreachable while pending (the dialog refuses every exit); here
            // it clears a refusal so the next open starts clean.
            setWithdrawDialog(null);
            withdrawBom.reset();
          }}
          testId="bom-withdraw-dialog"
        />
      ) : null}

      {archiveDialog !== null && archived === null ? (
        <ArchiveDialog
          version={bom.version}
          preview={archiveDialog}
          effectiveTo={bom.effective_to}
          pending={archiveBom.isPending}
          error={archiveBom.error}
          onConfirm={archive}
          onCancel={() => {
            setArchiveDialog(null);
            archiveBom.reset();
          }}
          testId="bom-archive-dialog"
        />
      ) : null}
    </section>
  );
}
